'use client';

import { FileText, Loader2, Paperclip, X } from 'lucide-react';
import * as React from 'react';
import { useRef } from 'react';

export interface FileChipProps {
	/** Nome del file allegato. */
	name: React.ReactNode;
	/** Chiamata al click sulla X per rimuovere l'allegato. */
	onRemove: () => void;
}

/** Chip rimovibile per un file allegato. */
const FileChip = ({ name, onRemove }: FileChipProps) => (
	<span className="inline-flex items-center gap-1 rounded bg-border/40 px-1.5 py-0.5 text-[11px] text-text-muted">
		{name}
		<button type="button" onClick={onRemove} aria-label="Rimuovi" className="hover:text-danger">
			<X className="h-2.5 w-2.5" />
		</button>
	</span>
);
FileChip.displayName = 'FileChip';

export interface AttachFilesButtonProps {
	/** Testo del bottone. Default: "Allega". */
	label?: React.ReactNode;
	/** Chiamata alla scelta di uno o più file, con l'elenco dei File selezionati. */
	onFiles: (files: File[]) => void;
	/** Tipi di file accettati (attributo `accept` dell'input nativo). */
	accept?: string;
	/** Se true (default), permette la selezione multipla. */
	multiple?: boolean;
	/** `inline` = bottone piccolo a chip; `dropzone` = bottone tratteggiato a larghezza intera. Default: `inline`. */
	variant?: 'inline' | 'dropzone';
}

/** Bottone per scegliere file da allegare; resetta l'input dopo la scelta così lo stesso file può essere riselezionato. */
const AttachFilesButton = ({
	label = 'Allega',
	onFiles,
	accept,
	multiple = true,
	variant = 'inline',
}: AttachFilesButtonProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const input = (
		<input
			ref={inputRef}
			type="file"
			multiple={multiple}
			accept={accept}
			className="hidden"
			onChange={(e) => {
				onFiles(Array.from(e.target.files ?? []));
				e.target.value = '';
			}}
		/>
	);

	if (variant === 'dropzone') {
		return (
			<>
				<button
					type="button"
					onClick={() => inputRef.current?.click()}
					className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-background py-2.5 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent"
				>
					<Paperclip className="h-3.5 w-3.5" />
					{label}
				</button>
				{input}
			</>
		);
	}

	return (
		<>
			<button
				type="button"
				onClick={() => inputRef.current?.click()}
				className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-border bg-card px-2 py-1 text-[12px] text-text-muted transition-colors hover:border-accent hover:text-accent"
			>
				<Paperclip className="h-3 w-3" />
				{label}
			</button>
			{input}
		</>
	);
};
AttachFilesButton.displayName = 'AttachFilesButton';

export interface AttachmentPillProps {
	/** Nome del file allegato. */
	name: React.ReactNode;
	/** Se presente, rende la pillola cliccabile per aprire/anteprima l'allegato. */
	onOpen?: () => void;
	/** Se presente, mostra la X per rimuovere l'allegato. */
	onRemove?: () => void;
}

/** Pillola bordata per un allegato, con icona documento; cliccabile e/o rimovibile a seconda delle callback passate. */
const AttachmentPill = ({ name, onOpen, onRemove }: AttachmentPillProps) => {
	const inner = (
		<>
			<FileText className="h-3.5 w-3.5 shrink-0 text-accent" />
			<span className="max-w-[160px] flex-1 truncate text-[13px] text-text-primary">{name}</span>
		</>
	);
	return (
		<div className="flex items-center gap-2 rounded-lg border border-accent/20 bg-accent/5 px-3 py-1.5">
			{onOpen ? (
				<button
					type="button"
					onClick={onOpen}
					className="flex min-w-0 flex-1 items-center gap-2 text-left transition-colors hover:text-accent"
				>
					{inner}
				</button>
			) : (
				inner
			)}
			{onRemove && (
				<button
					type="button"
					onClick={onRemove}
					aria-label="Rimuovi allegato"
					className="text-text-muted hover:text-danger"
				>
					<X className="h-3.5 w-3.5" />
				</button>
			)}
		</div>
	);
};
AttachmentPill.displayName = 'AttachmentPill';

export interface UploadAttachButtonProps {
	/** Se true, mostra lo spinner e disabilita il bottone (upload in corso). */
	uploading: boolean;
	/** Chiamata alla scelta di uno o più file, con la FileList selezionata. */
	onFiles: (files: FileList | null) => void;
	/** Tipi di file accettati (attributo `accept` dell'input nativo). */
	accept?: string;
	/** Se true (default), permette la selezione multipla. */
	multiple?: boolean;
	/** `dropzone` = tratteggiato a larghezza intera; `chip` = bottone bordato compatto. Default: `dropzone`. */
	variant?: 'dropzone' | 'chip';
	/** Testo mostrato quando non c'è un upload in corso. */
	idleLabel: React.ReactNode;
	/** Testo mostrato durante l'upload. Default: "Caricamento...". */
	uploadingLabel?: React.ReactNode;
}

/** Bottone di upload file con stato di caricamento (spinner + cambio testo). */
const UploadAttachButton = ({
	uploading,
	onFiles,
	accept,
	multiple = true,
	variant = 'dropzone',
	idleLabel,
	uploadingLabel = 'Caricamento...',
}: UploadAttachButtonProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	return (
		<>
			<button
				type="button"
				onClick={() => inputRef.current?.click()}
				disabled={uploading}
				className={
					variant === 'dropzone'
						? 'inline-flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-background py-3 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50'
						: 'inline-flex items-center gap-1.5 rounded-[7px] border border-border bg-card px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50'
				}
			>
				{uploading ? (
					<Loader2 className="h-3.5 w-3.5 animate-spin" />
				) : (
					<Paperclip className="h-3.5 w-3.5" />
				)}
				{uploading ? uploadingLabel : idleLabel}
			</button>
			<input
				ref={inputRef}
				type="file"
				multiple={multiple}
				accept={accept}
				className="hidden"
				onChange={(e) => {
					onFiles(e.target.files);
					e.target.value = '';
				}}
			/>
		</>
	);
};
UploadAttachButton.displayName = 'UploadAttachButton';

export { AttachFilesButton, AttachmentPill, FileChip, UploadAttachButton };
